/**
 * Segment event payload builders for the Contact Builder lead-form flow (NHS20-8253).
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `segmentTrack(...)`. Property names match the
 * Jira NHS20-8253 acceptance-criteria table exactly. `position` is provided by the caller (see
 * pages/communityDetail/components/eventTracking/) — this module has no opinion on what it means.
 *
 * @module leadGenerators/services/segmentEventBuilder
 */

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadButtonClickedEvent({ buttonName, position }) {
  return {
    eventName: 'Lead Button Clicked',
    customProperties: {
      button_action: buttonName,
      section_name: position,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} [params.date] - tour date, RequestATourForm only
 * @param {string} [params.preferredContactMethod]
 * @param {string} [params.realtorQuestion]
 * @param {number} params.timeToSubmit - seconds
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadModalFormSubmittedEvent({
  buttonName,
  position,
  date,
  preferredContactMethod,
  realtorQuestion,
  timeToSubmit,
}) {
  const customProperties = {
    button_action: buttonName,
    section_name: position,
    preferred_contact_method: preferredContactMethod,
    realtor_question: realtorQuestion,
    time_to_submit: timeToSubmit,
  };

  if (date) {
    customProperties.date = date;
  }

  return { eventName: 'Lead Modal Form Submitted', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {'x_button'|'outside_click'|'escape'} params.closeTarget
 * @param {number} params.fieldsCompletedCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadModalClosedEvent({
  buttonName,
  position,
  closeTarget,
  fieldsCompletedCount,
}) {
  return {
    eventName: 'Lead Modal Closed',
    customProperties: {
      button_action: buttonName,
      section_name: position,
      method: closeTarget,
      // form_started reflects whether the user actually entered data (fieldsCompletedCount > 0),
      // not just whether they focused a field — formStarted (leadFormTrackingState.js) goes true
      // on focus alone, so it can't answer "did they fill anything in". Matches GA4's form_close
      // click_target1 (googleAnalytics4EventBuilder.js).
      form_started: fieldsCompletedCount > 0,
      fields_completed_count: fieldsCompletedCount,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} params.errorType - 'validation'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormSubmissionFailedEvent({
  buttonName,
  position,
  errorType,
}) {
  return {
    eventName: 'Lead Form Submission Failed',
    customProperties: {
      button_action: buttonName,
      section_name: position,
      error_type: errorType,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string|null} params.lastFieldInteracted
 * @param {number} params.fieldsCompletedCount
 * @param {number} params.timeSpent - seconds
 * @param {boolean} params.hasValidationError
 * @param {'invalid_format'|'required_missing'} [params.errorType]
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormAbandonedEvent({
  buttonName,
  position,
  lastFieldInteracted,
  fieldsCompletedCount,
  timeSpent,
  hasValidationError,
  errorType,
}) {
  const customProperties = {
    button_action: buttonName,
    section_name: position,
    last_field_interacted: lastFieldInteracted,
    fields_completed_count: fieldsCompletedCount,
    time_spent: timeSpent,
    lead_field_error: hasValidationError,
  };

  if (hasValidationError) {
    customProperties.error_type = errorType;
  }

  return { eventName: 'Lead Form Abandoned', customProperties };
}

/**
 * "Get on the list" (Coming Soon "Get Notified") event payload builders (NHS20-8254). Bespoke
 * `Native Map Notify *` event names — the same convention
 * nativeInteractiveSiteMap/services/segmentEventBuilder.js already uses for its own events (e.g.
 * buildNotifyWhenAvailableClickedEvent → `native_map_notify_clicked`) — since the ticket's own
 * event vocabulary is already ISM-flavored, not a leadGenerators-specific one.
 */

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifySubmitClickedEvent() {
  return { eventName: 'Native Map Notify Submit Clicked', customProperties: {} };
}

/**
 * @param {object} params
 * @param {string} params.errorType - 'validation'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifySubmissionFailedEvent({ errorType }) {
  return {
    eventName: 'Native Map Notify Submission Failed',
    customProperties: { error_type: errorType },
  };
}

/**
 * @param {object} params
 * @param {'x_button'|'outside_click'|'escape'} params.closeTarget
 * @param {boolean} params.emailEntered
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyModalClosedEvent({ closeTarget, emailEntered }) {
  return {
    eventName: 'Native Map Notify Modal Closed',
    customProperties: { method: closeTarget, email_entered: emailEntered },
  };
}

/**
 * @param {object} params
 * @param {boolean} params.emailFieldInteracted
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyAbandonedEvent({ emailFieldInteracted }) {
  return {
    eventName: 'Native Map Notify Abandoned',
    customProperties: { email_field_interacted: emailFieldInteracted },
  };
}
